"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

/* The auth shell replaces the marketing header and footer on the sign-in journey.
   Everything here is deliberately quiet: one wordmark, one language control, one
   escape hatch to support, and a legal line at the bottom edge. The form is the
   only thing on the page with any weight. */
export default function AuthShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // The wordmark is the only way back out of registration, and it has two jobs.
  // Part-way through the flow it steps back to the start, which is what someone
  // clicking a logo mid-form nearly always means. From the first step there is
  // nothing left to step back to, so it leaves for the marketing site.
  const isInnerRegisterStep =
    pathname !== "/register" && pathname.startsWith("/register/");
  const homeHref = isInnerRegisterStep ? "/register" : "/";

  return (
    // The sign-in journey is light-only: it is read on a phone in daylight far more
    // often than the marketing site is, and the light palette is what the product's
    // own forms already assume. `data-theme` drives the token block in globals.css.
    <div
      data-layout="auth"
      data-theme="light"
      className="relative flex min-h-svh flex-col overflow-x-hidden bg-brand-bg text-brand-text"
    >
      {/* A single low wash behind the form so the near-black canvas reads as a lit
          surface rather than an unstyled page. Nothing sits on top of it. */}
      <div aria-hidden className="auth-ambient" />

      <header className="relative z-10">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-4 px-5 sm:h-20 sm:px-8">
          <Link
            href={homeHref}
            aria-label={isInnerRegisterStep ? "Back to the first step" : "Vell home"}
            className="global-border-radius -m-1 p-1 outline-none focus-visible:ring-2 focus-visible:ring-brand-green-strong/60"
          >
            <Image
              src="/logo-dr.png"
              alt="Vell"
              width={120}
              height={48}
              priority
              className="h-auto w-[78px] sm:w-[92px]"
            />
          </Link>

          {/* The language control is deliberately absent for now. `AuthLanguageSelect`
              in this folder still works and drops straight back in here. */}
          <div className="flex items-center">
            <Link
              href="/contact-us"
              className="-mr-3 rounded-full px-3 py-2 text-sm font-medium text-brand-text/70
                         transition-colors duration-200 hover:text-brand-text
                         focus-visible:text-brand-text focus-visible:outline-none
                         focus-visible:ring-2 focus-visible:ring-brand-green-strong/50"
            >
              Need help?
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex flex-1 items-center justify-center px-5 pb-14 pt-2 sm:pb-20 sm:pt-6">
        {children}
      </main>

      <footer className="relative z-10 px-5 pb-8 sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-brand-text/65">
          <span>&copy; {new Date().getFullYear()} Vell</span>
          <Link href="/legal/privacy-policy" className="transition-colors hover:text-brand-text">
            Privacy
          </Link>
          <Link
            href="/legal/terms-and-conditions"
            className="transition-colors hover:text-brand-text"
          >
            Terms
          </Link>
        </div>
      </footer>
    </div>
  );
}
